import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, CreditCard, X, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface LowCreditWarningProps {
  currentCredits: number;
  threshold?: number; // Default: 40 (1 video cost)
  onDismiss?: () => void;
}

export const LowCreditWarning = ({ 
  currentCredits, 
  threshold = 40,
  onDismiss 
}: LowCreditWarningProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  // Check localStorage for dismissed state (per session)
  useEffect(() => {
    const dismissedUntil = sessionStorage.getItem('lowCreditWarningDismissed');
    if (dismissedUntil) {
      const dismissTime = parseInt(dismissedUntil);
      if (Date.now() < dismissTime) {
        setIsDismissed(true);
      } else {
        sessionStorage.removeItem('lowCreditWarningDismissed');
      }
    }
  }, []);

  useEffect(() => {
    // Show warning if credits are low and not dismissed
    if (currentCredits > 0 && currentCredits < threshold && !isDismissed) {
      // Delay showing to avoid immediate popup
      timerRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    } else {
      setIsVisible(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentCredits, threshold, isDismissed]);

  const handleBuyCredits = () => {
    setIsVisible(false);
    navigate("/pricing");
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    // Dismiss for 1 hour
    sessionStorage.setItem('lowCreditWarningDismissed', String(Date.now() + 60 * 60 * 1000));
    onDismiss?.();
  };

  const getWarningLevel = () => {
    if (currentCredits === 0) return 'critical';
    if (currentCredits < 40) return 'critical';
    if (currentCredits < 80) return 'warning';
    return 'low';
  };

  const warningLevel = getWarningLevel();
  const videosRemaining = Math.floor(currentCredits / 40);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 400, 
            damping: 30 
          }}
          className="fixed top-20 right-4 z-[100] max-w-sm"
        >
          <div className={`rounded-xl shadow-2xl overflow-hidden border-2 ${
            warningLevel === 'critical' 
              ? 'bg-gradient-to-br from-red-500 to-red-600 border-red-400/30' 
              : 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-400/30'
          } text-white`}>
            {/* Header */}
            <div className="p-4 pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.15, 1],
                      rotate: warningLevel === 'critical' ? [0, -10, 10, 0] : 0
                    }}
                    transition={{ 
                      duration: 0.6,
                      repeat: warningLevel === 'critical' ? Infinity : 2,
                      repeatDelay: warningLevel === 'critical' ? 2 : 1
                    }}
                    className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
                  >
                    {warningLevel === 'critical' ? (
                      <AlertTriangle className="h-6 w-6" />
                    ) : (
                      <Coins className="h-6 w-6" />
                    )}
                  </motion.div>
                  
                  <div>
                    <motion.h3
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                      className="font-bold text-lg"
                    >
                      {warningLevel === 'critical' ? 'Kritik Kredi Uyarısı!' : 'Düşük Kredi Uyarısı'}
                    </motion.h3>
                    <motion.div
                      initial={{ y: -10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-2"
                    >
                      <span className="text-2xl font-bold">{currentCredits}</span>
                      <span className="text-sm text-white/80">kredi kaldı</span>
                    </motion.div>
                  </div>
                </div>
                
                <button 
                  onClick={handleDismiss}
                  className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="px-4 pb-3"
            >
              {videosRemaining === 0 ? (
                <p className="text-sm text-white/90">
                  ⚠️ Video oluşturmak için yeterli krediniz yok. Hemen kredi satın alın!
                </p>
              ) : videosRemaining === 1 ? (
                <p className="text-sm text-white/90">
                  Son <strong>1 video</strong> oluşturabilirsiniz. Kesintisiz kullanım için kredi yükleyin.
                </p>
              ) : (
                <p className="text-sm text-white/90">
                  Yaklaşık <strong>{videosRemaining} video</strong> daha oluşturabilirsiniz.
                </p>
              )}
            </motion.div>

            {/* Actions */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="px-4 pb-4 flex gap-2"
            >
              <Button
                onClick={handleBuyCredits}
                size="sm"
                className={`flex-1 font-semibold gap-2 ${
                  warningLevel === 'critical'
                    ? 'bg-white text-red-600 hover:bg-white/90'
                    : 'bg-white text-amber-600 hover:bg-white/90'
                }`}
              >
                <CreditCard className="h-4 w-4" />
                Kredi Satın Al
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 hover:text-white"
              >
                Sonra
              </Button>
            </motion.div>

            {/* Animated credit indicator */}
            <div className="h-1.5 bg-white/20 relative overflow-hidden">
              <motion.div
                className={`h-full ${
                  warningLevel === 'critical' ? 'bg-white/80' : 'bg-white/60'
                }`}
                style={{ width: `${Math.min(100, (currentCredits / 120) * 100)}%` }}
                animate={warningLevel === 'critical' ? {
                  opacity: [1, 0.5, 1]
                } : {}}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Hook for checking low credit status
export const useLowCreditCheck = (credits: number, threshold: number = 80) => {
  const isLowCredit = credits > 0 && credits < threshold;
  const isCriticalCredit = credits >= 0 && credits < 40;
  const videosRemaining = Math.floor(credits / 40);
  
  return {
    isLowCredit,
    isCriticalCredit,
    videosRemaining,
    threshold
  };
};